import { EXIT_TYPE } from '../constants/tradingConstants';

/**
 * Map an `exit_type` enum value to a human-readable label for UI display.
 *
 * Used by surfaces that render the exit type as a "Type" row or badge —
 * `StrategyPositionsModalV2` (Pro view), `StrategyPositionsModal` (Classic view),
 * and the LegSection `typeLabel` prop they both feed into.
 *
 * Returns `null` when no exit_type is set so the caller's `typeLabel && (...)`
 * guard correctly hides the row entirely while a position is in flight
 * (e.g. EXIT_PENDING before close_position writes the final exit_type). The
 * modals pass a claimed UL exit's pending type instead (utils/ulSpotLevels
 * `ulExitType`), so that row shows while the UL exit is still EXIT_PENDING.
 *
 * Pass-through behavior for SL / TP / EOD / EXIT preserves existing UX where
 * the raw enum is the desired short label.
 *
 * Note: `RecentTradesCard.tsx` intentionally uses its own dashboard-specific
 * label map (e.g. EXIT → "Target") and is NOT a consumer of this helper.
 */
export const exitTypeLabel = (exitType: string | null | undefined): string | null => {
  if (!exitType) return null;
  if (exitType === EXIT_TYPE.EXPIRY_SETTLED) return 'Settled at expiry';
  if (exitType === EXIT_TYPE.EXIT_FORCED) return 'Force-closed';
  if (exitType === EXIT_TYPE.STRATEGY_TP) return 'Strategy Target Hit';
  if (exitType === EXIT_TYPE.STRATEGY_SL) return 'Strategy Stop Hit';
  if (exitType === EXIT_TYPE.HEDGE_RECYCLE) return 'Hedge recycled';
  // Phase 5e.M3 — UL (underlying-based) exits. Distinct labels flag the
  // spot-side origin so users know the exit fired off index movement, not
  // option premium. UL_SL/UL_TP are emitted by underlying_monitor via
  // take_profit_check_handler Phase D (spot crossed underlying_sl_level /
  // reached underlying_target_level). Kept short so the StrategyPositions*
  // "Type" row stays legible.
  if (exitType === EXIT_TYPE.UL_SL) return 'Underlying SL hit';
  if (exitType === EXIT_TYPE.UL_TP) return 'Underlying Target hit';
  return exitType;
};